import { Row } from "@tanstack/react-table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { DataTable } from "@/components/ui/data-table";
import { User, userColumns } from "@/lib/columns";
import { axiosPrivate } from "@/api/axios";
import { fetchAllUsers } from "@/api/users";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LoadingTable } from "@/components/ui/LoadingSpinner";
import UserDetailDialog from "../user/UserDetailDialog";

function Experts() {
  const queryClient = useQueryClient();
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["users", "experts"],
    queryFn: async () => {
      const users = await fetchAllUsers();
      return users.filter((user) => user.role === "expert");
    },
    placeholderData: [],
  });

  const removeExpertMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await axiosPrivate.put(`/users/${userId}`, {
        role: "user",
      });
      return { userId, res };
    },
    onSuccess: ({ userId }) => {
      toast.success(`Expert with ID ${userId} is removed from Expert`);
      queryClient.invalidateQueries({ queryKey: ["users", "experts"] });
    },
    onError: () => {
      toast.error("Something went wrong on the server side");
    },
  });

  // Actions column for removing expert role
  const actions = {
    id: "actions",
    header: "Actions",
    cell: ({ row }: { row: Row<User> }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                setSelectedUser(row.original);
                setDetailOpen(true);
              }}
            >
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => removeExpertMutation.mutate(row.original.id)}
              disabled={removeExpertMutation.isPending}
            >
              Remove Expert
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  };

  const columns = React.useMemo(() => [...userColumns, actions], [removeExpertMutation.isPending]);

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Expert Management</h1>
      <p>
        Manage all experts in one place. View, filter, and monitor expert users.
      </p>
      {isLoading ? (
        <LoadingTable message="Loading experts..." />
      ) : (
        <DataTable
          columns={columns}
          data={data ?? []}
          placeholder="Filter Experts"
          filterColumns={["phone_number"]}
        />
      )}
      <UserDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        user={selectedUser}
      />
    </div>
  );
}

export default Experts;
